'use client';

/**
 * 1:1 Meeting Assistant
 * Help CTOs run effective 1:1s with structured templates and action tracking
 */

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import ToolSEO from '@/components/ToolSEO';
import { useAuth } from '@/lib/auth/AuthContext';
import {
  TeamMember,
  MeetingWithMember,
  ActionItem,
  MeetingType,
  MEETING_TYPE_CONFIG,
  CreateTeamMemberRequest,
} from '@/lib/one-on-one/types';
import * as api from '@/lib/one-on-one/api';
import { TeamMemberCard } from '@/components/one-on-one/TeamMemberCard';
import { MeetingCard } from '@/components/one-on-one/MeetingCard';
import { ActionItemList } from '@/components/one-on-one/ActionItemList';

// Tier access levels
const TIER_HIERARCHY = { free: 0, pro: 1, team: 2 };

type UserTier = 'free' | 'pro' | 'team';

function getFeatureAccess(tier: UserTier) {
  const tierLevel = TIER_HIERARCHY[tier];
  return {
    canEdit: tierLevel >= TIER_HIERARCHY.free,
    canSave: tierLevel >= TIER_HIERARCHY.pro,
    maxTeamMembers: tier === 'free' ? 3 : tier === 'pro' ? 15 : Infinity,
    maxMeetingsPerMonth: tier === 'free' ? 10 : Infinity,
    historyMonths: tier === 'free' ? 1 : tier === 'pro' ? 12 : Infinity,
    canUseAI: tierLevel >= TIER_HIERARCHY.pro,
    canExport: tierLevel >= TIER_HIERARCHY.pro,
  };
}

export default function OneOnOnePage() {
  const router = useRouter();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // State
  const [userTier] = useState<UserTier>('pro'); // TODO: Get from auth
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState<MeetingWithMember[]>([]);
  const [recentMeetings, setRecentMeetings] = useState<MeetingWithMember[]>([]);
  const [pendingActions, setPendingActions] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialogs
  const [showAddMember, setShowAddMember] = useState(false);
  const [showScheduleMeeting, setShowScheduleMeeting] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<MeetingWithMember | null>(null);

  // Form state
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('');
  const [newMeetingType, setNewMeetingType] = useState<MeetingType>('regular_1on1');
  const [newMeetingDate, setNewMeetingDate] = useState('');

  const features = getFeatureAccess(userTier);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [members, upcoming, actions] = await Promise.all([
        api.listTeamMembers(),
        api.getUpcomingMeetings(),
        api.getPendingActionItems(),
      ]);

      setTeamMembers(members);
      setUpcomingMeetings(upcoming);
      setPendingActions(actions);

      // Get recent completed meetings
      const allMeetings = await api.listMeetings();
      const completed = allMeetings
        .filter(m => m.completed_at)
        .slice(0, 5);
      setRecentMeetings(completed);
    } catch (err) {
      console.error('[OneOnOne] Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadData();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, loadData]);

  // Handlers
  const handleAddMember = async () => {
    if (!newMemberName.trim()) return;

    if (teamMembers.length >= features.maxTeamMembers) {
      setError(`Free tier limited to ${features.maxTeamMembers} team members. Upgrade to Pro for more.`);
      return;
    }

    try {
      const request: CreateTeamMemberRequest = {
        name: newMemberName.trim(),
        metadata: newMemberRole ? { role: newMemberRole.trim() } : {},
      };
      const newMember = await api.createTeamMember(request);
      setTeamMembers(prev => [...prev, newMember]);
      setShowAddMember(false);
      setNewMemberName('');
      setNewMemberRole('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add team member');
    }
  };

  const handleScheduleMeeting = async () => {
    if (!selectedMemberId) return;

    try {
      await api.createMeeting({
        team_member_id: selectedMemberId,
        meeting_type: newMeetingType,
        scheduled_at: newMeetingDate || undefined,
      });
      await loadData();
      setShowScheduleMeeting(false);
      setSelectedMemberId(null);
      setNewMeetingDate('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule meeting');
    }
  };

  const handleActionUpdate = async (id: string, updates: Partial<ActionItem>) => {
    try {
      await api.updateActionItem(id, updates);
      setPendingActions(prev =>
        prev.map(a => a.id === id ? { ...a, ...updates } : a)
          .filter(a => a.status === 'pending' || a.status === 'in_progress')
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update action item');
    }
  };

  const handleActionDelete = async (id: string) => {
    try {
      await api.deleteActionItem(id);
      setPendingActions(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete action item');
    }
  };

  // Show login prompt for unauthenticated users
  if (!authLoading && !isAuthenticated) {
    return (
      <>
        <ToolSEO
          toolName="1:1 Meeting Assistant"
          toolSlug="one-on-one"
          description="Run effective 1:1s with structured templates, AI-suggested talking points, and action item tracking."
          category="planner"
          isPremium={true}
        />
        <main className="min-h-screen bg-background text-foreground py-12 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl font-bold mb-4">1:1 Meeting Assistant</h1>
            <p className="text-foreground-secondary mb-8">
              Run effective 1:1s with structured templates, AI-suggested talking points,
              and action item tracking.
            </p>
            <div className="bg-cream/5 border border-border rounded-lg p-8">
              <p className="text-foreground-tertiary mb-4">
                Sign in to access your team members, meeting history, and action items.
              </p>
              <a
                href="/login?returnTo=/tools/one-on-one"
                className="inline-block px-6 py-3 bg-accent text-accent-foreground font-medium rounded hover:bg-accent-hover transition-colors"
              >
                Sign In to Continue
              </a>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <ToolSEO
        toolName="1:1 Meeting Assistant"
        toolSlug="one-on-one"
        description="Run effective 1:1s with structured templates, AI-suggested talking points, and action item tracking."
        category="planner"
        isPremium={true}
      />

      <main className="min-h-screen bg-background text-foreground py-12 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold">1:1 Meeting Assistant</h1>
              <p className="text-foreground-secondary mt-2">
                Run effective 1:1s with structured templates and action tracking
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddMember(true)}
                className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium hover:bg-accent-hover transition-colors"
              >
                + Add Team Member
              </button>
            </div>
          </div>

          {/* Tier badge */}
          <div className="mb-6 flex items-center gap-4">
            <span className={`
              px-3 py-1 rounded text-sm font-medium
              ${userTier === 'team' ? 'bg-purple-500/20 text-purple-400' :
                userTier === 'pro' ? 'bg-accent-muted text-accent' :
                'bg-cream/10 text-foreground-tertiary'}
            `}>
              {userTier.toUpperCase()} Plan
            </span>
            {userTier === 'free' && (
              <span className="text-sm text-foreground-tertiary">
                {teamMembers.length}/{features.maxTeamMembers} team members
              </span>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
              {error}
              <button onClick={() => setError(null)} className="ml-4 underline">Dismiss</button>
            </div>
          )}

          {/* Loading state */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-foreground-tertiary">Loading your 1:1 data...</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left column: Team Members */}
              <div className="lg:col-span-1">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span>👥</span> Team Members
                </h2>

                {teamMembers.length === 0 ? (
                  <div className="border border-dashed border-border rounded-lg p-6 text-center">
                    <p className="text-foreground-tertiary mb-4">No team members yet</p>
                    <button
                      onClick={() => setShowAddMember(true)}
                      className="text-accent hover:underline"
                    >
                      Add your first team member
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {teamMembers.map(member => {
                      const memberMeetings = recentMeetings.filter(
                        m => m.team_member_id === member.id
                      );
                      const memberActions = pendingActions.filter(
                        a => memberMeetings.some(m => m.id === a.meeting_id)
                      );

                      return (
                        <TeamMemberCard
                          key={member.id}
                          member={member}
                          lastMeeting={memberMeetings[0]}
                          pendingActions={memberActions.length}
                          onClick={() => {
                            // TODO: Navigate to member detail page
                            console.log('View member:', member.id);
                          }}
                          onScheduleMeeting={() => {
                            setSelectedMemberId(member.id);
                            setShowScheduleMeeting(true);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Middle column: Meetings */}
              <div className="lg:col-span-1">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span>📅</span> Upcoming Meetings
                </h2>

                {upcomingMeetings.length === 0 ? (
                  <div className="border border-dashed border-border rounded-lg p-6 text-center mb-6">
                    <p className="text-foreground-tertiary">No upcoming meetings</p>
                  </div>
                ) : (
                  <div className="space-y-3 mb-6">
                    {upcomingMeetings.slice(0, 5).map(meeting => (
                      <MeetingCard
                        key={meeting.id}
                        meeting={meeting}
                        onClick={() => setSelectedMeeting(meeting)}
                      />
                    ))}
                  </div>
                )}

                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span>✅</span> Recent Meetings
                </h2>

                {recentMeetings.length === 0 ? (
                  <div className="border border-dashed border-border rounded-lg p-6 text-center">
                    <p className="text-foreground-tertiary">No completed meetings yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentMeetings.map(meeting => (
                      <MeetingCard
                        key={meeting.id}
                        meeting={meeting}
                        onClick={() => setSelectedMeeting(meeting)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Right column: Action Items */}
              <div className="lg:col-span-1">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span>📋</span> Pending Action Items
                </h2>

                <div className="border border-border rounded-lg p-4">
                  <ActionItemList
                    items={pendingActions}
                    onUpdate={handleActionUpdate}
                    onDelete={handleActionDelete}
                    canEdit={features.canEdit}
                  />
                </div>

                {/* Quick stats */}
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="bg-cream/5 border border-border-muted rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-accent">{teamMembers.length}</div>
                    <div className="text-sm text-foreground-tertiary">Team Members</div>
                  </div>
                  <div className="bg-cream/5 border border-border-muted rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-accent">{pendingActions.length}</div>
                    <div className="text-sm text-foreground-tertiary">Pending Actions</div>
                  </div>
                </div>

                {/* AI feature callout */}
                {!features.canUseAI && (
                  <div className="mt-6 bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span>✨</span>
                      <span className="font-medium text-purple-400">AI Suggestions</span>
                    </div>
                    <p className="text-sm text-foreground-tertiary mb-3">
                      Upgrade to Pro for AI-powered talking point suggestions based on
                      meeting history and context.
                    </p>
                    <button className="text-sm text-purple-400 hover:underline">
                      Upgrade to Pro →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add Team Member Dialog */}
          {showAddMember && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-background border border-border rounded-lg max-w-md w-full p-6">
                <h3 className="text-xl font-semibold mb-4">Add Team Member</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-foreground-secondary mb-1">Name *</label>
                    <input
                      type="text"
                      value={newMemberName}
                      onChange={e => setNewMemberName(e.target.value)}
                      placeholder="e.g., Jane Smith"
                      className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-secondary mb-1">Role (optional)</label>
                    <input
                      type="text"
                      value={newMemberRole}
                      onChange={e => setNewMemberRole(e.target.value)}
                      placeholder="e.g., Senior Engineer"
                      className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowAddMember(false);
                      setNewMemberName('');
                      setNewMemberRole('');
                    }}
                    className="px-4 py-2 text-foreground-secondary hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddMember}
                    disabled={!newMemberName.trim()}
                    className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium disabled:opacity-50"
                  >
                    Add Member
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Schedule Meeting Dialog */}
          {showScheduleMeeting && selectedMemberId && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-background border border-border rounded-lg max-w-md w-full p-6">
                <h3 className="text-xl font-semibold mb-4">Schedule Meeting</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-foreground-secondary mb-1">With</label>
                    <div className="px-3 py-2 bg-cream/5 border border-border-muted rounded text-foreground">
                      {teamMembers.find(m => m.id === selectedMemberId)?.name}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-secondary mb-2">Meeting Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      {(Object.keys(MEETING_TYPE_CONFIG) as MeetingType[]).map(type => {
                        const config = MEETING_TYPE_CONFIG[type];
                        return (
                          <button
                            key={type}
                            onClick={() => setNewMeetingType(type)}
                            className={`
                              p-3 text-left rounded border transition-colors
                              ${newMeetingType === type
                                ? 'border-accent bg-accent-muted'
                                : 'border-border hover:border-border/40'
                              }
                            `}
                          >
                            <div className="flex items-center gap-2">
                              <span>{config.icon}</span>
                              <span className="text-sm font-medium">{config.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-secondary mb-1">
                      Date & Time (optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={newMeetingDate}
                      onChange={e => setNewMeetingDate(e.target.value)}
                      className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowScheduleMeeting(false);
                      setSelectedMemberId(null);
                      setNewMeetingDate('');
                    }}
                    className="px-4 py-2 text-foreground-secondary hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleScheduleMeeting}
                    className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium"
                  >
                    Schedule
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Meeting Detail Dialog */}
          {selectedMeeting && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-background border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">
                        {MEETING_TYPE_CONFIG[selectedMeeting.meeting_type].icon}
                      </span>
                      <h3 className="text-xl font-semibold">
                        {MEETING_TYPE_CONFIG[selectedMeeting.meeting_type].label}
                      </h3>
                    </div>
                    <p className="text-foreground-tertiary">
                      with {selectedMeeting.team_member?.name}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedMeeting(null)}
                    className="p-2 text-foreground-tertiary hover:text-foreground"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Meeting details */}
                <div className="space-y-6">
                  {selectedMeeting.scheduled_at && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground-secondary mb-1">Scheduled</h4>
                      <p>{new Date(selectedMeeting.scheduled_at).toLocaleString()}</p>
                    </div>
                  )}

                  {selectedMeeting.summary && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground-secondary mb-1">Summary</h4>
                      <p className="text-foreground-secondary">{selectedMeeting.summary}</p>
                    </div>
                  )}

                  {selectedMeeting.mood_rating && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground-secondary mb-1">Mood Rating</h4>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">
                          {'😊😐😕'.charAt(Math.min(2, 3 - Math.ceil(selectedMeeting.mood_rating / 2)))}
                        </span>
                        <span>{selectedMeeting.mood_rating}/5</span>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    {features.canExport && (
                      <button
                        onClick={() => {
                          if (selectedMeeting.team_member) {
                            api.downloadMeetingNotes(selectedMeeting.id, selectedMeeting.team_member.name);
                          }
                        }}
                        className="px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 transition-colors"
                      >
                        Export Notes
                      </button>
                    )}
                    <button
                      onClick={() => {
                        router.push(`/tools/one-on-one/meetings?id=${selectedMeeting.id}`);
                      }}
                      className="px-4 py-2 bg-accent text-accent-foreground rounded font-medium"
                    >
                      Open Meeting
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <footer className="mt-12 pt-8 border-t border-border-muted text-center text-sm text-foreground-tertiary">
            <p>
              1:1 Meeting Assistant helps you run effective one-on-ones with structured
              templates and action tracking.
            </p>
            <p className="mt-2">
              Based on best practices from engineering leadership.
            </p>
          </footer>
        </div>
      </main>
    </>
  );
}
